import type {
  BrandsResponse,
  CampaignListResponse,
  CampaignResponse,
  CancelCampaignResponse,
  ClientFailuresRecentResponse,
  ClientFailuresSummaryResponse,
  DailyMetricsFreshnessResponse,
  DeliveriesResponse,
  DispatchCampaignResponse,
  DmarcSummaryResponse,
  EmailMetricsResponse,
  EmailQuotaStatus,
  FirstPaymentCohortsResponse,
  FunnelMetricsResponse,
  LiveResponse,
  MailTriageListResponse,
  PluginDriftRecentResponse,
  PluginFailuresRecentResponse,
  PluginFailuresSummaryResponse,
  QuotaExceededHitsResponse,
  RecentVisitsResponse,
  RecordingCapHitsResponse,
  SendTestResponse,
  SnapshotResponse,
  SuccessRateByVersionResponse,
  TestResult,
  UnitEconomicsResponse,
} from "@voice2mail/shared/v2m_api";
import { AdminApi, Configuration, ResponseError, VigilApi } from "@voice2mail/shared/v2m_api";

import type {
  AttributionSummaryResponseWithMaterialBreakdown,
  BillingObservability,
  CreateCampaignRequest,
  CreatePromptPayload,
  Generation,
  GiftPayload,
  MailTriageRecord,
  MailTriageUrgency,
  Model,
  ModelMetrics,
  OverviewMetrics,
  PerRequestStats,
  Prompt,
  PromptMetrics,
  RecordingDurationResponse,
  TimeseriesResponse,
  UpdatePromptPayload,
  UserDetails,
  UserListItem,
  UserListResponse,
  UserSearchResult,
} from "../types/api";

// ---------------------------------------------------------------------------
// Environment configuration
// ---------------------------------------------------------------------------

export type Environment = "dev" | "prod" | "test";

export interface EnvironmentConfig {
  /** Human-readable label shown in the UI. */
  label: string;
  /** Base URL for all API requests in this environment. Empty string means not configured. */
  url: string;
  /** Firestore database name so the operator knows which DB they are viewing. */
  dbLabel: string;
  /** When true the environment is unavailable and must not be selectable. */
  disabled: boolean;
}

/**
 * Central config map: environment key → backend details.
 *
 * Base URLs are injected at build time (vite `define`) so deploy-time env vars
 * can override them; no host literals live in the source. test is intentionally
 * disabled — there is no test backend.
 */
export const ENV_CONFIG: Record<Environment, EnvironmentConfig> = {
  dev: {
    label: "Development",
    url: __V2M_ADMIN_API_BASE_URL_DEV__,
    dbLabel: "voice2mail-dev",
    disabled: false,
  },
  prod: {
    label: "Production",
    url: __V2M_ADMIN_API_BASE_URL__,
    dbLabel: "default",
    disabled: false,
  },
  test: {
    label: "Test",
    url: "",
    dbLabel: "—",
    disabled: true,
  },
};

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

/** localStorage key holding the operator's last-selected environment. */
const ENV_STORAGE_KEY = "v2m.admin.environment";

/** Default when nothing is persisted. Prod keeps the deployed admin safe by default. */
const DEFAULT_ENV: Environment = "prod";

/**
 * Read the persisted environment synchronously at module load.
 *
 * This MUST resolve before any consumer reads the active environment —
 * Firebase init (auth/firebase.ts) and the first data fetch both read it on
 * mount. Without an early synchronous read the panel would start in the
 * default and silently snap back to it after a hard reload, pointing auth and
 * all requests at the wrong backend.
 */
function readPersistedEnv(): Environment {
  try {
    const stored = localStorage.getItem(ENV_STORAGE_KEY);
    if (stored === "dev" || stored === "prod" || stored === "test") {
      if (!ENV_CONFIG[stored].disabled) return stored;
    }
  } catch {
    // localStorage unavailable (private mode, SSR, tests) — fall back to default.
  }
  return DEFAULT_ENV;
}

let currentEnv: Environment = readPersistedEnv();
let getIdToken: (() => Promise<string>) | null = null;

/** Subscribers are notified synchronously after every setEnvironment call. */
const envSubscribers = new Set<() => void>();

// ---------------------------------------------------------------------------
// Public environment API
// ---------------------------------------------------------------------------

/**
 * Subscribe to environment changes.
 * Returns an unsubscribe function (pass it to useEffect cleanup).
 */
export function subscribeToEnvironment(cb: () => void): () => void {
  envSubscribers.add(cb);
  return () => {
    envSubscribers.delete(cb);
  };
}

export function setEnvironment(env: Environment): void {
  if (ENV_CONFIG[env].disabled) return;
  currentEnv = env;
  try {
    localStorage.setItem(ENV_STORAGE_KEY, env);
  } catch {
    // Persisting is best-effort; an unavailable store must not break switching.
  }
  envSubscribers.forEach((cb) => cb());
}

export function getEnvironment(): Environment {
  return currentEnv;
}

export function setTokenProvider(provider: () => Promise<string>): void {
  getIdToken = provider;
}

// ---------------------------------------------------------------------------
// Generated-client wiring
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Build a Configuration for the current environment.
 *
 * The base URL comes from the active environment selection; the Firebase ID
 * token is attached through the generated client's `accessToken` hook (which
 * the runtime turns into an `Authorization: Bearer <token>` header). The
 * generated client targets the versioned `/v1/...` paths natively.
 */
function adminConfiguration(): Configuration {
  return new Configuration({
    basePath: ENV_CONFIG[currentEnv].url,
    accessToken: async () => {
      if (!getIdToken) {
        throw new Error("Token provider not set. Call setTokenProvider first.");
      }
      return getIdToken();
    },
  });
}

function adminApi(): AdminApi {
  return new AdminApi(adminConfiguration());
}

/**
 * VigilApi shares the same Configuration (base URL + Bearer token) as
 * AdminApi — GET /v1/vigil/* lives on the same voice2mail_proxy backend,
 * gated by the same get_admin_user dependency (viewer role is sufficient,
 * every Vigil endpoint is read-only). No separate host or auth wiring.
 */
function vigilApi(): VigilApi {
  return new VigilApi(adminConfiguration());
}

/**
 * Run a generated-client call, normalising backend errors to ApiError so the
 * rest of the panel can read `.status`.
 */
async function call<T>(fn: () => Promise<unknown>): Promise<T> {
  try {
    return (await fn()) as T;
  } catch (error) {
    return await toApiError(error);
  }
}

async function toApiError(error: unknown): Promise<never> {
  if (error instanceof ResponseError) {
    let message = error.response.statusText;
    try {
      const body = (await error.response.json()) as { detail?: string };
      if (body?.detail) message = body.detail;
    } catch {
      // Body was not JSON — keep the status text.
    }
    throw new ApiError(error.response.status, message);
  }
  throw error instanceof Error ? error : new Error(String(error));
}

// ---------------------------------------------------------------------------
// Prompts
// ---------------------------------------------------------------------------

export async function listPrompts(): Promise<Prompt[]> {
  const data = await call<{ prompts: Prompt[] }>(() => adminApi().listPromptsV1AdminPromptsGet());
  return data.prompts;
}

export async function createPrompt(payload: CreatePromptPayload): Promise<Prompt> {
  return call<Prompt>(() =>
    adminApi().createPromptV1AdminPromptsPost({ createPromptRequest: payload })
  );
}

export async function updatePrompt(id: string, payload: UpdatePromptPayload): Promise<Prompt> {
  return call<Prompt>(() =>
    adminApi().updatePromptV1AdminPromptsPromptIdPatch({
      promptId: id,
      updatePromptRequest: payload,
    })
  );
}

export async function disablePrompt(id: string): Promise<{ status: string; id: string }> {
  return call<{ status: string; id: string }>(() =>
    adminApi().disablePromptV1AdminPromptsPromptIdDelete({ promptId: id })
  );
}

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

export async function listModels(): Promise<Model[]> {
  const data = await call<{ models: Model[] }>(() => adminApi().listModelsV1AdminModelsGet());
  return data.models;
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export async function getOverviewMetrics(): Promise<OverviewMetrics> {
  return call<OverviewMetrics>(() => adminApi().getOverviewMetricsV1AdminMetricsOverviewGet());
}

export async function getPromptMetrics(): Promise<PromptMetrics[]> {
  const data = await call<{ prompts: PromptMetrics[] }>(() =>
    adminApi().getPromptMetricsV1AdminMetricsPromptsGet()
  );
  return data.prompts;
}

export async function getModelMetrics(): Promise<ModelMetrics[]> {
  const data = await call<{ models: ModelMetrics[] }>(() =>
    adminApi().getModelMetricsV1AdminMetricsModelsGet()
  );
  return data.models;
}

export async function getGenerations(params?: {
  limit?: number;
  promptId?: string;
  modelId?: string;
  status?: string;
}): Promise<{ generations: Generation[]; count: number }> {
  return call<{ generations: Generation[]; count: number }>(() =>
    adminApi().getGenerationRecordsV1AdminMetricsGenerationsGet({
      limit: params?.limit,
      promptId: params?.promptId,
      modelId: params?.modelId,
      status: params?.status,
    })
  );
}

export async function getTimeseries(days = 30): Promise<TimeseriesResponse> {
  return call<TimeseriesResponse>(() =>
    adminApi().getTimeseriesMetricsV1AdminMetricsTimeseriesGet({ days })
  );
}

export async function getPerRequestStats(days = 30, top = 20): Promise<PerRequestStats> {
  return call<PerRequestStats>(() =>
    adminApi().getPerRequestStatsV1AdminMetricsPerRequestStatsGet({ days, top })
  );
}

export async function getRecordingDurationStats(days = 1): Promise<RecordingDurationResponse> {
  return call<RecordingDurationResponse>(() =>
    adminApi().getRecordingDurationDistributionV1AdminMetricsRecordingDurationGet({ days })
  );
}

/**
 * M-C (V2M-DEV-012): distinct-user + total-event counts for the recording
 * hard-cap stop. Endpoint existed with zero panel consumer before this call
 * — see `RecordingCapHits` component for the view this feeds.
 */
export async function getRecordingCapHits(days = 1): Promise<RecordingCapHitsResponse> {
  return call<RecordingCapHitsResponse>(() =>
    adminApi().getRecordingCapHitsV1AdminMetricsRecordingCapHitsGet({ days })
  );
}

/**
 * Signal #1 (V2M-DEV-012): distinct-user + total-event counts for hitting
 * the quota ceiling. Before this call, `quota_exceeded` reached ONLY
 * Plausible (aggregate-only, no per-user model) via
 * `src.middleware.quota._fire_quota_exceeded` — zero admin-panel endpoint,
 * zero consumer. See `QuotaExceededHits` component for the view this feeds.
 */
export async function getQuotaExceededHits(days = 1): Promise<QuotaExceededHitsResponse> {
  return call<QuotaExceededHitsResponse>(() =>
    adminApi().getQuotaExceededHitsV1AdminMetricsQuotaExceededHitsGet({ days })
  );
}

/**
 * V2M-DEV-163: per-customer cost/token unit economics. Existed on the backend
 * (src/api/admin/metrics.py `get_unit_economics`) with zero panel consumer —
 * grep in this file found not even a wrapper function. See `UnitEconomics`
 * component for the view this feeds, including the honest disclosure it
 * renders when the backend gives no signal to separate a real zero-usage
 * window from `daily_user_metrics` never having been written for it.
 */
export async function getUnitEconomics(days = 1, top = 20): Promise<UnitEconomicsResponse> {
  return call<UnitEconomicsResponse>(() =>
    adminApi().getUnitEconomicsV1AdminMetricsUnitEconomicsGet({ days, top })
  );
}

/**
 * V2M-DEV-163: acquisition-funnel stages E4 (registration -> activation) and
 * E5 (activation -> first payment). Same zero-consumer gap as
 * `getUnitEconomics` above. Both stages are computed live, all-time, from the
 * primary `users`/`generations`/`revenue_ledger` collections (no `days`
 * window, no downstream aggregation pipeline that could go stale) — see
 * `AcquisitionFunnel` component for how it still tells a real 0% rate apart
 * from "no denominator to measure a rate from at all".
 */
export async function getFunnelMetrics(): Promise<FunnelMetricsResponse> {
  return call<FunnelMetricsResponse>(() => adminApi().getFunnelMetricsV1AdminMetricsFunnelGet());
}

/**
 * V2M-DEV-163: live "is daily_metrics still being written" check (V2M-DEV-080
 * on the backend). Same zero-consumer gap as the two functions above — this
 * is the one of the three endpoints whose entire payload IS the
 * zero-vs-never-measured signal (`hoursSinceLastWrite: null` means the
 * lookback window never saw a write at all); see `DailyMetricsFreshness`
 * component.
 */
export async function getDailyMetricsFreshness(): Promise<DailyMetricsFreshnessResponse> {
  return call<DailyMetricsFreshnessResponse>(() =>
    adminApi().getDailyMetricsFreshnessV1AdminMetricsDailyMetricsFreshnessGet()
  );
}

/**
 * V2M-DEV-012, signal #5 ("firstPaidAt / firstPaidTier") — the one signal in
 * the 12-signal metrics project that had zero code at all before this
 * endpoint (`src/api/admin/metrics.py::get_first_payment_cohorts`) landed.
 * All-time, no `days` window — see that endpoint's docstring for why. See
 * `FirstPaymentCohorts` component for how `writeFailuresTotal` lets the
 * panel tell a genuinely measured zero apart from silently-failing
 * `revenue_ledger` writes.
 */
export async function getFirstPaymentCohorts(top = 20): Promise<FirstPaymentCohortsResponse> {
  return call<FirstPaymentCohortsResponse>(() =>
    adminApi().getFirstPaymentCohortsV1AdminMetricsFirstPaymentCohortsGet({ top })
  );
}

/**
 * V2M-DEV-012, signal #12 second half ("success rate per wersja"). The backend
 * endpoint (`src/api/admin/metrics.py::get_success_rate_by_version`) landed in
 * commit 94ea921c and then sat for a day with ZERO panel consumers — a grep
 * for `successRate` across `frontend/voice2mail_admin/src` returned nothing,
 * not even a wrapper. The task criterion says "widoczne w panelu", so an
 * unconsumed endpoint is an unmet requirement, not a cosmetic gap. See
 * `SuccessRateByVersion` component for the view this feeds, including how
 * `hoursSinceLastEventEver === null` separates "quiet window" from
 * "`generations` never written to at all".
 */
export async function getSuccessRateByVersion(days = 1): Promise<SuccessRateByVersionResponse> {
  return call<SuccessRateByVersionResponse>(() =>
    adminApi().getSuccessRateByVersionV1AdminMetricsSuccessRateByVersionGet({ days })
  );
}

// ---------------------------------------------------------------------------
// Production failures (V2M-DEV-010): plugin_failure_reports/
// client_failure_reports were WRITE-ONLY before this change — real-time
// ntfy push only, no admin-panel read view (checkpoint
// 2026-07-25-v2m-telemetria-nie-widzimy-produkcji.md, SEDNO point 6). The
// backend read endpoints (`src/api/admin/plugin_failures.py`,
// `client_failures.py`) already existed and were already in the generated
// client — this file's gap was that nothing in the panel called them.
// ---------------------------------------------------------------------------

export async function getPluginFailuresSummary(): Promise<PluginFailuresSummaryResponse> {
  return call<PluginFailuresSummaryResponse>(() =>
    adminApi().getPluginFailuresSummaryV1AdminPluginFailuresSummaryGet()
  );
}

export async function getRecentPluginFailures(limit = 20): Promise<PluginFailuresRecentResponse> {
  return call<PluginFailuresRecentResponse>(() =>
    adminApi().getRecentPluginFailuresV1AdminPluginFailuresRecentGet({ limit })
  );
}

export async function getClientFailuresSummary(): Promise<ClientFailuresSummaryResponse> {
  return call<ClientFailuresSummaryResponse>(() =>
    adminApi().getClientFailuresSummaryV1AdminClientFailuresSummaryGet()
  );
}

export async function getRecentClientFailures(limit = 20): Promise<ClientFailuresRecentResponse> {
  return call<ClientFailuresRecentResponse>(() =>
    adminApi().getRecentClientFailuresV1AdminClientFailuresRecentGet({ limit })
  );
}

// ---------------------------------------------------------------------------
// Billing pipeline health (webhook observability)
// ---------------------------------------------------------------------------

export async function getBillingObservability(): Promise<BillingObservability> {
  return call<BillingObservability>(() =>
    adminApi().getBillingObservabilityV1AdminBillingObservabilityGet()
  );
}

/** Re-run the billing-pipeline health check on demand (the Refresh button). */
export async function refreshBillingObservability(): Promise<BillingObservability> {
  return call<BillingObservability>(() =>
    adminApi().refreshBillingObservabilityV1AdminBillingObservabilityRefreshPost()
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export async function searchUsers(email: string): Promise<UserSearchResult[]> {
  const data = await call<{ users: UserSearchResult[] }>(() =>
    adminApi().searchUsersV1AdminUsersSearchGet({ email })
  );
  return data.users;
}

export async function getUserDetails(uid: string): Promise<UserDetails> {
  return call<UserDetails>(() => adminApi().getUserDetailsV1AdminUsersUidGet({ uid }));
}

export async function giftGenerations(
  uid: string,
  payload: GiftPayload
): Promise<{ status: string }> {
  return call<{ status: string }>(() =>
    adminApi().giftGenerationsV1AdminUsersUidGiftPost({ uid, giftRequest: payload })
  );
}

/**
 * List users with cursor-based pagination.
 *
 * Backend contract: GET /admin/users?limit=N&cursor=X
 * Response: { users: UserListItem[], nextCursor?: string }
 */
export async function listUsers(params?: {
  limit?: number;
  cursor?: string;
}): Promise<UserListResponse> {
  return call<UserListResponse>(() =>
    adminApi().listUsersV1AdminUsersGet({ limit: params?.limit, cursor: params?.cursor })
  );
}

/** Max page size the backend accepts for GET /v1/admin/users (users.py: `le=100`). */
const USERS_PAGE_LIMIT = 100;

/**
 * Safety cap on `listAllUsers`'s page loop — at `USERS_PAGE_LIMIT` this is
 * 2,000 users, comfortably above current scale (tens of users). A cap exists
 * so a runaway collection turns into a visible, bounded truncation notice
 * (see `UsersPage`'s truncated banner) instead of an unbounded fetch loop.
 */
const USERS_MAX_PAGES = 20;

export interface ListAllUsersResult {
  users: UserListItem[];
  /** True when the page cap was hit before `nextCursor` ran out — the set below is a prefix, not the whole collection. */
  truncated: boolean;
}

/**
 * Fetch every user by paging through the cursor internally, so the panel can
 * offer real full-dataset sorting instead of client-side sort-lies-on-one-page
 * (PK 2026-07-25: "jeśli tabela ma paginację, sortowanie musi iść do źródła
 * danych albo paginacja musi zniknąć"). At current scale (tens of users) this
 * is a handful of round-trips; if the users collection grows into the
 * thousands, replace this with a real server-side `sort`/`order_by` query
 * param instead (v2m-backend scope) rather than paging further.
 */
export async function listAllUsers(): Promise<ListAllUsersResult> {
  const all: UserListItem[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < USERS_MAX_PAGES; page++) {
    const result = await listUsers({ limit: USERS_PAGE_LIMIT, cursor });
    all.push(...result.users);
    if (!result.nextCursor) return { users: all, truncated: false };
    cursor = result.nextCursor;
  }
  return { users: all, truncated: true };
}

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

export async function getAttributionSummary(
  days = 30
): Promise<AttributionSummaryResponseWithMaterialBreakdown> {
  return call<AttributionSummaryResponseWithMaterialBreakdown>(() =>
    adminApi().getAttributionSummaryV1AdminAttributionSummaryGet({ days })
  );
}

export async function listRecentVisits(limit = 50): Promise<RecentVisitsResponse> {
  return call<RecentVisitsResponse>(() =>
    adminApi().listRecentVisitsV1AdminAttributionVisitsGet({ limit })
  );
}

// ---------------------------------------------------------------------------
// Mail triage
// ---------------------------------------------------------------------------

export async function listMailTriageRecords(params?: {
  category?: string;
  urgency?: MailTriageUrgency;
  limit?: number;
  cursor?: string;
}): Promise<MailTriageListResponse> {
  return call<MailTriageListResponse>(() =>
    adminApi().listMailTriageRecordsV1AdminMailTriageGet({
      category: params?.category,
      urgency: params?.urgency,
      limit: params?.limit,
      cursor: params?.cursor,
    })
  );
}

export async function getDmarcSummary(): Promise<DmarcSummaryResponse> {
  return call<DmarcSummaryResponse>(() =>
    adminApi().getDmarcSummaryV1AdminMailTriageDmarcSummaryGet()
  );
}

/** Max page size the backend accepts for GET /v1/admin/mail-triage (mail_triage.py: `le=200`). */
const MAIL_TRIAGE_PAGE_LIMIT = 200;

/**
 * Safety cap on `listAllMailTriageRecords`'s page loop — at
 * `MAIL_TRIAGE_PAGE_LIMIT` this is 4,000 records, comfortably above current
 * scale (prod `mail_triage` = 252 docs measured 2026-07-25, dev = 0). A cap
 * exists so a runaway collection turns into a visible, bounded truncation
 * notice (see `MailTriagePage`'s truncated banner) instead of an unbounded
 * fetch loop.
 */
const MAIL_TRIAGE_MAX_PAGES = 20;

export interface ListAllMailTriageRecordsResult {
  records: MailTriageRecord[];
  /** True when the page cap was hit before `nextCursor` ran out — the set below is a prefix, not the whole filtered set. */
  truncated: boolean;
}

/**
 * Fetch every mail-triage record matching the given filters by paging through
 * the cursor internally — same reasoning as `listAllUsers`: a table backed by
 * cursor pagination cannot support honest client-side column sort (sorting
 * would silently apply to one page only), so this replaces "load a page" with
 * "load the whole filtered set" and lets the UI sort that in full. At current
 * scale (hundreds of records) this is a small, bounded number of round-trips;
 * if the collection grows much larger, replace with real server-side sort
 * params (v2m-backend scope) instead of paging further.
 */
export async function listAllMailTriageRecords(params?: {
  category?: string;
  urgency?: MailTriageUrgency;
}): Promise<ListAllMailTriageRecordsResult> {
  const all: MailTriageRecord[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAIL_TRIAGE_MAX_PAGES; page++) {
    const result = await listMailTriageRecords({
      category: params?.category,
      urgency: params?.urgency,
      limit: MAIL_TRIAGE_PAGE_LIMIT,
      cursor,
    });
    all.push(...result.records);
    if (!result.nextCursor) return { records: all, truncated: false };
    cursor = result.nextCursor;
  }
  return { records: all, truncated: true };
}

// ---------------------------------------------------------------------------
// Campaigns (migrated from voice2mail_webpage's orphaned /admin/campaigns —
// that route's same-origin Firebase auth was removed when the webpage's
// standalone admin login was retired, leaving it unreachable. This panel's
// single-email-gated auth + api.x2mail.eu backend replaces it.)
//
// NOTE: the campaigns/{id}/preview endpoint was never implemented in the
// backend (404 in production), so there is no preview call here.
// ---------------------------------------------------------------------------

export async function createCampaign(data: CreateCampaignRequest): Promise<CampaignResponse> {
  return call<CampaignResponse>(() =>
    adminApi().createCampaignV1AdminCampaignsPost({
      createCampaignRequest: {
        // The generated model types `template` as the backend enum; the wizard
        // only ever sends a supported template, so narrow the local string.
        template: data.template as "legal_change",
        documentVersion: data.document_version,
        params: data.params,
        audienceFilter: data.audience_filter
          ? {
              planTier: data.audience_filter.plan_tier ?? null,
              registeredBefore: data.audience_filter.registered_before
                ? new Date(data.audience_filter.registered_before)
                : null,
              registeredAfter: data.audience_filter.registered_after
                ? new Date(data.audience_filter.registered_after)
                : null,
            }
          : null,
      },
    })
  );
}

export async function listCampaigns(): Promise<CampaignResponse[]> {
  const data = await call<CampaignListResponse>(() =>
    adminApi().listCampaignsV1AdminCampaignsGet()
  );
  return data.campaigns;
}

export async function getCampaign(campaignId: string): Promise<CampaignResponse> {
  return call<CampaignResponse>(() =>
    adminApi().getCampaignV1AdminCampaignsCampaignIdGet({ campaignId })
  );
}

export async function sendTestEmail(campaignId: string): Promise<TestResult[]> {
  const data = await call<SendTestResponse>(() =>
    adminApi().sendTestV1AdminCampaignsCampaignIdTestPost({ campaignId })
  );
  return data.results;
}

export async function dispatchCampaign(campaignId: string): Promise<DispatchCampaignResponse> {
  return call<DispatchCampaignResponse>(() =>
    adminApi().dispatchCampaignV1AdminCampaignsCampaignIdDispatchPost({ campaignId })
  );
}

/**
 * Cancel a campaign. The backend's response is intentionally NOT typed as
 * `CampaignResponse` — `cancel_campaign` in campaigns.py returns only
 * `{"status": "cancelled", "id": campaign_id}`, not the full updated
 * document. Callers that need the fresh CampaignResponse (counts, status,
 * etc.) must follow up with `getCampaign(campaignId)`.
 */
export async function cancelCampaign(campaignId: string): Promise<CancelCampaignResponse> {
  return call<CancelCampaignResponse>(() =>
    adminApi().cancelCampaignV1AdminCampaignsCampaignIdCancelPost({ campaignId })
  );
}

export async function getDeliveries(campaignId: string): Promise<DeliveriesResponse> {
  return call<DeliveriesResponse>(() =>
    adminApi().getDeliveriesV1AdminCampaignsCampaignIdDeliveriesGet({ campaignId })
  );
}

export async function getEmailQuota(): Promise<EmailQuotaStatus> {
  return call<EmailQuotaStatus>(() => adminApi().getQuotaV1AdminQuotaGet());
}

// ---------------------------------------------------------------------------
// Brand Health (Vigil) — absorbed from the standalone products/x2mail-admin
// SPA (AgiEu repo) per PK's single-panel consolidation directive (2026-07-15).
// Read-only observability: pair-watch decision grid, live traffic, per-brand
// DNS/cert/mail health, and the email-service metrics placeholder.
// ---------------------------------------------------------------------------

export async function getVigilSnapshot(): Promise<SnapshotResponse> {
  return call<SnapshotResponse>(() => vigilApi().getSnapshotV1VigilSnapshotGet());
}

export async function getVigilLive(): Promise<LiveResponse> {
  return call<LiveResponse>(() => vigilApi().getLiveV1VigilLiveGet());
}

export async function getVigilBrands(): Promise<BrandsResponse> {
  return call<BrandsResponse>(() => vigilApi().getBrandsV1VigilBrandsGet());
}

export async function getVigilEmail(): Promise<EmailMetricsResponse> {
  return call<EmailMetricsResponse>(() => vigilApi().getEmailV1VigilEmailGet());
}

// ---------------------------------------------------------------------------
// Plugin selector-drift telemetry (2026-07-20 alerting-audit P1 fix — the
// plugin_reactive_fetches collection was write-only until this endpoint).
// AdminApi, not VigilApi: this is core-product plugin telemetry, not the
// cross-brand DNS/cert/traffic health that Vigil covers.
// ---------------------------------------------------------------------------

export async function getPluginDriftRecent(params?: {
  days?: number;
  limit?: number;
  provider?: string;
  pluginVersion?: string;
}): Promise<PluginDriftRecentResponse> {
  return call<PluginDriftRecentResponse>(() =>
    adminApi().getRecentPluginDriftV1AdminPluginDriftRecentGet({
      days: params?.days,
      limit: params?.limit,
      provider: params?.provider,
      pluginVersion: params?.pluginVersion,
    })
  );
}
